/**
 * Lightweight Stockfish loader (lite single-threaded WASM from /stockfish/).
 * Supports level-aware move selection (UCI Skill Level) and position evaluation.
 */

function levelProfile(level = 3) {
  const lv = Math.max(1, Math.min(10, Number(level) || 3));
  // Map 1..10 → Stockfish Skill Level 0..20 + depth/time budget
  if (lv <= 2) {
    return { depth: 5, skill: 0, movetime: 200, multipv: 1 };
  }
  if (lv <= 4) {
    return { depth: 8, skill: 5, movetime: 350, multipv: 1 };
  }
  if (lv <= 7) {
    return { depth: 11, skill: 12, movetime: 600, multipv: 1 };
  }
  return { depth: Math.min(16, 10 + lv), skill: 20, movetime: 900, multipv: 1 };
}

export function createStockfishWorker() {
  const worker = new Worker('/stockfish/stockfish-18-lite-single.js');
  let ready = false;
  const queue = [];
  let searchToken = 0;

  worker.onmessage = (e) => {
    const line = typeof e.data === 'string' ? e.data : String(e.data ?? '');
    if (line === 'uciok' || line.startsWith('readyok')) {
      ready = true;
    }
    queue.forEach((fn) => fn(line));
  };

  const send = (cmd) => worker.postMessage(cmd);

  send('uci');
  send('isready');

  const waitReady = () =>
    new Promise((resolve) => {
      if (ready) {
        resolve();
        return;
      }
      const handler = (line) => {
        if (line === 'uciok' || line.startsWith('readyok')) {
          ready = true;
          resolve();
        }
      };
      queue.push(handler);
      send('isready');
    });

  const stop = () => {
    searchToken += 1;
    try {
      send('stop');
    } catch {
      /* ignore */
    }
  };

  const getBestMove = async (fen, depth = 12, opts = {}) => {
    await waitReady();
    const myToken = ++searchToken;
    const movetime = opts.movetime != null ? Number(opts.movetime) : null;
    const skill = opts.skill != null ? Number(opts.skill) : null;
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        try { send('stop'); } catch { /* ignore */ }
        reject(new Error('Stockfish timeout'));
      }, Math.max(15000, (movetime || 0) + 12000));
      const handler = (line) => {
        if (line.startsWith('bestmove')) {
          cleanup();
          if (myToken !== searchToken) {
            resolve(null);
            return;
          }
          const parts = line.split(/\s+/);
          const bm = parts[1];
          if (!bm || bm === '(none)') {
            resolve(null);
          } else {
            resolve(bm);
          }
        }
      };
      const cleanup = () => {
        clearTimeout(timeout);
        const idx = queue.indexOf(handler);
        if (idx >= 0) queue.splice(idx, 1);
      };
      queue.push(handler);
      send('ucinewgame');
      send('setoption name MultiPV value 1');
      if (skill != null && Number.isFinite(skill)) {
        send(`setoption name Skill Level value ${Math.max(0, Math.min(20, Math.round(skill)))}`);
      }
      send(`position fen ${fen}`);
      if (movetime != null && movetime > 0) {
        send(`go movetime ${Math.max(50, Math.floor(movetime))}`);
      } else {
        send(`go depth ${depth}`);
      }
    });
  };

  /**
   * Level-aware move via UCI Skill Level (+ optional clock movetime).
   * @returns {Promise<string|null>} UCI move
   */
  const getMoveForLevel = async (fen, level = 3, opts = {}) => {
    const profile = levelProfile(level);
    let movetime = opts.movetime != null ? opts.movetime : profile.movetime;
    if (opts.clockMs != null && opts.clockMs > 0) {
      // Spend ~3–8% of remaining clock, capped by profile
      const budget = Math.min(profile.movetime * 2, Math.max(80, Math.floor(opts.clockMs * 0.05)));
      movetime = Math.min(movetime, budget);
    }
    return getBestMove(fen, profile.depth, { skill: profile.skill, movetime });
  };

  const evaluateFen = async (fen, depth, multipvCount) => {
    const goDepth = depth == null ? 12 : depth;
    await waitReady();
    const myToken = ++searchToken;
    const mpv = Math.max(1, Math.min(5, Number(multipvCount) || 1));
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        try { send('stop'); } catch { /* ignore */ }
        reject(new Error('Stockfish timeout'));
      }, Math.max(22000, goDepth * 2500));
      const linesByPv = new Map();
      const handler = (line) => {
        if (line.startsWith('info ') && line.includes(' score ') && line.includes(' pv ')) {
          const multipvMatch = line.match(/\bmultipv (\d+)/);
          const pvIndex = multipvMatch ? Number(multipvMatch[1]) : 1;
          const scoreMate = line.match(/\bscore mate (-?\d+)/);
          const scoreCpMatch = line.match(/\bscore cp (-?\d+)/);
          const pvIdx = line.indexOf(' pv ');
          const pvTokens = pvIdx >= 0
            ? line.slice(pvIdx + 4).trim().split(/\s+/).filter((t) => /^[a-h][1-8][a-h][1-8][qrbn]?$/i.test(t)).slice(0, 6)
            : [];
          const firstUci = pvTokens[0] || null;
          linesByPv.set(pvIndex, {
            uci: firstUci,
            pv: pvTokens,
            scoreCp: scoreMate ? null : (scoreCpMatch ? Number(scoreCpMatch[1]) : null),
            mate: scoreMate ? Number(scoreMate[1]) : null,
          });
        }
        if (line.startsWith('bestmove')) {
          cleanup();
          if (myToken !== searchToken) {
            reject(new Error('Stockfish cancelled'));
            return;
          }
          const ranked = [...linesByPv.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([, v]) => v);
          const top = ranked[0] || {};
          const parts = line.split(/\s+/);
          const bm = parts[1];
          resolve({
            bestMove: (bm && bm !== '(none)' ? bm : top.uci) || null,
            scoreCp: top.scoreCp ?? 0,
            mate: top.mate ?? null,
            lines: ranked,
          });
        }
      };
      const cleanup = () => {
        clearTimeout(timeout);
        const idx = queue.indexOf(handler);
        if (idx >= 0) queue.splice(idx, 1);
      };
      queue.push(handler);
      send('ucinewgame');
      send(`setoption name MultiPV value ${mpv}`);
      send('setoption name Skill Level value 20');
      send(`position fen ${fen}`);
      send(`go depth ${goDepth}`);
    });
  };

  const dispose = () => {
    searchToken += 1;
    try {
      worker.terminate();
    } catch {
      /* ignore */
    }
  };

  return { getBestMove, getMoveForLevel, evaluateFen, stop, dispose, send };
}
